<template>
  <div class="u-flex u-align-center u-max-w-full">
    <blob-panel>
      <h1 class="text-center">
        Sign up
      </h1>

      <form role="form" @submit.prevent="submit">
        <div class="form-group" :class="formClass('name')">
          <label for="name">
            Name
          </label>
          <input
            id="name"
            v-model="user.name"
            name="name"
            type="text"
            class="form-control"
            autocomplete="name"
            required
            autofocus
            @input="updateScore"
          >

          <span v-for="error in errors['name']" v-if="errors['name']" class="help-block">{{ error }}<br></span>
        </div>

        <div class="form-group" :class="formClass('email')">
          <label for="email">Email</label>
          <input
            id="email"
            v-model="user.email"
            name="email"
            type="email"
            class="form-control"
            autocomplete="email"
            required
            @input="updateScore"
          >

          <span v-for="error in errors['email']" v-if="errors['email']" class="help-block">{{ error }}<br></span>
        </div>

        <div class="form-group" :class="formClass('password')">
          <label>Password</label>
          <vue-password
            ref="password"
            v-model="user.password"
            name="password"
            autocomplete="password"
            :user-inputs="[user.name, user.email]"
            required
            @input="updateScore"
          />
          <span v-for="error in errors['password']" v-if="errors['password']" class="help-block">{{ error }}<br></span>
          <span v-if="feedback" class="help-block" v-text="feedback" />
        </div>

        <div class="form-group">
          <label for="password-confirm">Confirm password</label>
          <input
            id="password-confirm"
            v-model="user.password_confirmation"
            name="password-confirm"
            type="password"
            class="form-control"
            required
            autocomplete="password-confirm"
          >
        </div>

        <template v-if="providerSettings.authorization.requireCompanyInformation">
          <hr>

          <div class="form-group">
            <label for="company_type">Company type</label>
            <select
              id="company_type"
              v-model="user.company_type"
              class="form-control"
              name="company_type"
              type="text"
              :class="formClass('company_type')"
            >
              <option value="" disabled selected>
                Company type
              </option>
              <option
                v-for="option in company_options.company_types"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
            <span v-for="error in errors['company_type']" v-if="errors['company_type']" class="help-block"><strong v-text="error" /><br></span>
          </div>

          <div class="form-group">
            <label for="company_size">
              Company size
            </label>
            <select
              id="company_size"
              v-model="user.company_size"
              class="form-control"
              name="company_size"
              type="text"
              :class="formClass('company_size')"
            >
              <option value="" disabled selected>
                Company size
              </option>
              <option
                v-for="option in company_options.company_size"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
            <span v-for="error in errors['company_size']" v-if="errors['company_size']" class="help-block"><strong v-text="error" /><br></span>
          </div>

          <div class="form-group">
            <label for="country">
              Country
            </label>
            <select
              id="country"
              v-model="user.country"
              class="form-control"
              name="country"
              type="text"
              :class="formClass('country')"
              autocomplete="country"
            >
              <option value="" disabled selected>
                Country
              </option>
              <option v-for="country in company_options.countries" :key="country" :value="country">
                {{ country }}
              </option>
            </select>
            <span v-for="error in errors['country']" v-if="errors['country']" class="help-block"><strong v-text="error" /><br></span>
          </div>

          <hr>
        </template>

        <div v-if="providerSettings.should_agree_to_terms" class="checkbox u-mb-small" :class="formClass('agree_terms')">
          <label>
            <input v-model="user.agree_terms" type="checkbox" required>
            I agree that my use of the Services and/or the Blendr.io Solution, and the processing of my Personal Data shall be governed by the <a href="https://blendr.io/terms" target="_blank" rel="noopener">Blendr.io Terms and Conditions</a>.
          </label>
          <span v-if="errors['agree_terms']" class="help-block"><strong>The terms and agreements must be accepted.</strong></span>
        </div>

        <div class="u-mt-x-large">
          <button type="submit" class="btn btn--primary" :disabled="!isValid">
            Sign up
          </button>
        </div>
      </form>
    </blob-panel>

    <a href="/login" class="emphasis-link u-mb-x-large">
      Already have an account? Sign in here
    </a>
  </div>
</template>

<script>
import { axios } from '@qlik-trial/automations-ui'
import countries from '../../consts/countries'
import { mapGetters } from 'vuex'
import { MessageLevel } from '@qlik-trial/automations-ui'

export default {
  components: { VuePassword: () => import(/* webpackChunkName: "vue-password" */'./VuePassword') },
  data() {
    return {
      user: {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        company_type: '',
        company_size: '',
        country: '',
      },
      company_options: {
        company_types: ['Saas', 'Agency/Consultancy/Integrator', 'Enterprise', 'SME', 'Other'],
        company_size: ['1 employee', '2-5 employees', '6-20 employees', '21-50 employees', '51-500 employees', '500+ employees'],
        countries: countries,
      },
      errors: {},
      cancel: null,
      feedback: null,
    }
  },

  computed: {
    ...mapGetters({
      providerSettings: 'providerSettings',
    }),

    isValid() {
      return this.user.password && this.user.password.length
          && this.user.password_confirmation && this.user.password_confirmation.length
          && !this.feedback
    },
  },

  methods: {
    submit() {
      this.feedback = null
      this.errors = {}

      axios.post('/register', this.user, {
        errorHandling: { 429: false },
      })
        .then(() => {
          window.location.href = '/'
        })
        .catch(error => {
          this.errors = error.response.data

          if (error.response.status === 429) {
            this.flash({
              message: 'Too many requests have been made. Please try again later.',
              level: MessageLevel.WARNING,
            })
          }
        })
    },

    updateScore() {
      if (this.cancel) {
        this.cancel()
      }

      this.errors = {}

      this.$nextTick(() => {
        this.feedback = this.user.password.length
          ? this.$refs && this.$refs.password && this.$refs.password.feedback
          : null
      })
    },

    formClass(name) {
      if (name === 'password' && this.feedback) {
        return 'has-error'
      }

      if (this.errors[name]) {
        return 'has-error'
      }
    },
  },
}
</script>
